import { useFeatureFlags } from '@brightdrop/feature-flags-client';
import { Box, Link, TextField, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { translateText } from '~/common/components/TranslatedLabel/TranslatedLabel';
import { FEEDBACK_ATTACHMENTS_FLAG } from '~/common/models/featureFlags.model';
import { SUPPORT_CONTACT_PATH } from '~/common/models/pages/supportPages.model';
import FeedbackAttachment from './FeedbackAttachment';
import { FeedbackStarRating } from './FeedbackStarRating';
import type { FeedbackAttachmentItem } from './feedbackAttachment.types';

export type FeedbackFormContentClassNames = {
  formContent: string;
  formFieldsBlock: string;
  formFieldsBlockInner: string;
  ratingLabel: string;
  ratingRow: string;
  messageLabel: string;
  outlinedMessageField: string;
  supportDisclaimer: string;
  supportLink: string;
};

export type FeedbackFormContentProps = {
  value: string;
  isSubmitting: boolean;
  errorMessage: string | undefined;
  onValueChange: (value: string) => void;
  starValue: number | undefined;
  onStarChange: (value: number | undefined) => void;
  classes: FeedbackFormContentClassNames;
  placeholder: string;
  commentsLabel: string;
  /** Called when the user follows the in-app support link (close modal and navigate). */
  onSupportNavigate: () => void;
  attachmentItems: FeedbackAttachmentItem[];
  uploadSizeError: boolean;
  onAttachmentFilesSelected: (
    selected: File | FileList | File[] | null
  ) => void;
  onAttachmentSizeError: () => void;
  onRemoveAttachment: (id: string) => void;
};

const RATING_GROUP_LABEL_ID = 'feedback-rating-group-label';

const TRANSLATION_KEYS = {
  rateExperience: 'feedback:cta.rateExperience',
  contactSupportLine: 'feedback:cta.contactSupportLine',
} as const;

/**
 * Feedback dialog body: optional star rating, comment field, and support disclaimer.
 */
export function FeedbackFormContent({
  value,
  isSubmitting,
  errorMessage,
  onValueChange,
  starValue,
  onStarChange,
  classes: c,
  placeholder,
  commentsLabel,
  onSupportNavigate,
  attachmentItems,
  uploadSizeError,
  onAttachmentFilesSelected,
  onAttachmentSizeError,
  onRemoveAttachment,
}: FeedbackFormContentProps) {
  const { t } = useTranslation();
  const { getFlag } = useFeatureFlags();
  const isFeedbackAttachmentsFlagEnabled = getFlag(FEEDBACK_ATTACHMENTS_FLAG);

  return (
    <Box className={c.formContent}>
      <Box className={c.formFieldsBlock}>
        <Box className={c.formFieldsBlockInner}>
          <div>
            <Typography
              id={RATING_GROUP_LABEL_ID}
              component="span"
              className={c.ratingLabel}
            >
              {translateText(TRANSLATION_KEYS.rateExperience)}
            </Typography>
            <FeedbackStarRating
              value={starValue}
              onChange={onStarChange}
              disabled={isSubmitting}
              aria-labelledby={RATING_GROUP_LABEL_ID}
              rowClassName={c.ratingRow}
            />
          </div>
          <div>
            <Typography
              component="label"
              htmlFor="feedback-textarea"
              className={c.messageLabel}
            >
              {commentsLabel}
            </Typography>
            <TextField
              id="feedback-textarea"
              data-testid="feedback-textarea"
              className={c.outlinedMessageField}
              placeholder={placeholder}
              value={value}
              onChange={(e) => onValueChange(e.target.value)}
              disabled={isSubmitting}
              multiline
              minRows={4}
              maxRows={12}
              variant="outlined"
              fullWidth
              margin="none"
              error={Boolean(errorMessage)}
              helperText={errorMessage}
              slotProps={{
                formHelperText: errorMessage
                  ? {
                      'data-testid': 'feedback-error',
                      role: 'alert',
                    }
                  : undefined,
                htmlInput: {
                  'data-heap-id': 'feedback-textarea-focus',
                },
              }}
            />
          </div>
          {isFeedbackAttachmentsFlagEnabled && (
            <FeedbackAttachment
              attachmentItems={attachmentItems}
              uploadSizeError={uploadSizeError}
              onFilesSelected={onAttachmentFilesSelected}
              onSizeError={onAttachmentSizeError}
              onRemoveAttachment={onRemoveAttachment}
            />
          )}
        </Box>
        <Typography className={c.supportDisclaimer} component="p">
          <Trans
            i18nKey={TRANSLATION_KEYS.contactSupportLine}
            t={t}
            components={[
              <Link
                key="support"
                className={c.supportLink}
                component={RouterLink}
                to={SUPPORT_CONTACT_PATH}
                data-testid="feedback-support-link"
                underline="hover"
                onClick={onSupportNavigate}
              />,
            ]}
          />
        </Typography>
      </Box>
    </Box>
  );
}
